/*
 * -----------------------------------------------------------------------
 *  <copyright file="settings-menu/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { GridColumnMenuItemContent, GridColumnMenuItemGroup } from '@progress/kendo-react-grid';
import { RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import React, { FormEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import { IExpandableGridColumnV2 } from '../../expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import HeadingMenu from '../heading-menu';
import ItemsPerPage from '../items-per-page';
import MenuButton from './menu-button';
import MenuContent from './menu-content';
import { SettingsMenuProps, SettingsMenuTypes } from './settings-menu.interface';
import { useBoolean, useOnClickOutside, useTranslation } from '@hooks/index';
import { compareColumnsByOrderIndex } from '@utils/grid';

const SettingsMenu = ({
    columns,
    gridKey,
    onColumnsSubmit,
    removeHideFilter,
    group,
    setGroup,
    showPager,
    pageSize,
    setCurrentPage,
    handleChangePageSize,
    disabled,
}: SettingsMenuProps) => {
    const { t } = useTranslation();
    const recPerPageExpanded = useBoolean(true);
    const columnExpanded = useBoolean(false);
    const lockExpanded = useBoolean(false);
    const groupExpanded = useBoolean(false);
    const showMenu = useBoolean(false);
    const settingsMenuRef = useRef<HTMLDivElement | null>(null);
    const [columnsArray, setColumnsArray] = useState<IExpandableGridColumnV2[]>([]);

    useOnClickOutside(settingsMenuRef, () => showMenu.setFalse());

    const closeMenuItems = () => {
        recPerPageExpanded.setValue(false);
        columnExpanded.setValue(false);
        groupExpanded.setValue(false);
        lockExpanded.setValue(false);
    };

    const closeMenu = () => {
        closeMenuItems();
        showMenu.setFalse();
    };

    const onToggleMenu = () => {
        if (showMenu.value) {
            closeMenu();
        } else {
            showMenu.setTrue();
        }
    };

    const onMenuItemClick = (itemToToggle: SetStateAction<any>) => {
        closeMenuItems();
        itemToToggle.setValue(!itemToToggle.value);
    };

    useEffect(() => {
        // Uncheck checkboxes when group items were removed from the group bar
        setColumnsArray(
            columnsArray.map((column) => {
                if (!group.find((item) => item.id === column.id)) {
                    column.grouped = false;
                }
                return column;
            }),
        );
    }, [group]);

    const updateListOfColumns = () => {
        const newArr: IExpandableGridColumnV2[] = [];

        columns.forEach((item) => {
            // show not only parent columns but the child as well
            if (item.children) {
                const doubleChildrenItems: any = [];

                const childrenItems = item.children.map((childrenItem) => {
                    if (childrenItem.children?.length) {
                        childrenItem.children.forEach((doubleChildrenItem) => {
                            doubleChildrenItems.push({
                                ...doubleChildrenItem,
                                isChild: true,
                                parent: childrenItem.id,
                            });
                        });

                        return {
                            ...childrenItem,
                            isParent: true,
                            parent: childrenItem.id,
                            children: doubleChildrenItems,
                        };
                    }
                    return { ...childrenItem, isChild: true, parent: item.id };
                });
                newArr.push({ ...item, isParent: true }, ...childrenItems, ...doubleChildrenItems);
            } else {
                newArr.push({ ...item });
            }
        });

        setColumnsArray(newArr);
    };

    useEffect(() => {
        updateListOfColumns();
    }, [columns]);

    const childrenVisibility = (
        item: IExpandableGridColumnV2,
        items: IExpandableGridColumnV2[],
    ): IExpandableGridColumnV2 => {
        item.children = item.children?.map((el) => {
            // find column which you want to hide
            if (el.children?.length) {
                return childrenVisibility(el, items);
            }

            const child = items.find((element) => element.id === el.id);

            return {
                ...el,
                show: child?.show,
            };
        });
        const visibleChildren = item.children?.filter((el) => el.show === true) || [];

        return {
            ...item,
            show: (item.children?.length || 0) - (visibleChildren?.length || 0) !== (item.children?.length || 0),
        };
    };

    const onSubmit = (key: SettingsMenuTypes) => (event: FormEvent, items: IExpandableGridColumnV2[]) => {
        if (event) {
            event.preventDefault();
        }
        if (gridKey) {
            if (key === SettingsMenuTypes.lock) {
                const nonChildItems = items.filter((item) => !item.isChild);
                onColumnsSubmit(nonChildItems);
            } else if (key === SettingsMenuTypes.group) {
                setGroup(items.filter((item) => item.grouped));
            } else {
                const result = columns.map((item) => {
                    if (item.children?.length) {
                        return childrenVisibility(item, items);
                    }
                    // find column which you want to hide
                    const element = items.find((el) => item.id === el.id);
                    return {
                        ...item,
                        show: element?.show,
                    };
                });

                onColumnsSubmit(result);
            }
        }
        setColumnsArray(items);
        closeMenu();
    };

    const onReset = (key: string) => (event: FormEvent) => {
        event.preventDefault();
        let allColumns = [...columns];
        if (key === SettingsMenuTypes.lock) {
            allColumns = columns.map((col) => {
                if (col.children?.length)
                    col.children = col.children.map((item) => ({
                        ...item,
                        locked: false,
                    }));
                return {
                    ...col,
                    locked: col.lockedByDefault !== undefined ? col.lockedByDefault : false,
                };
            });
        } else if (key === SettingsMenuTypes.group) {
            setGroup([]);
        } else {
            allColumns = columns.map((col) => {
                if (col.children?.length)
                    col.children = col.children.map((item) => ({
                        ...item,
                        show: col.showByDefault !== undefined ? col.showByDefault : true,
                    }));
                return {
                    ...col,
                    show: col.showByDefault !== undefined ? col.showByDefault : true,
                };
            });
        }
        onColumnsSubmit(allColumns);
    };

    const oneVisibleColumn = columnsArray.filter((c) => c.show).length === 1;

    const onItemsPerPageChange = (event: RadioGroupChangeEvent) => {
        setCurrentPage(1);
        handleChangePageSize(+event.value);
        showMenu.setFalse();
    };

    return (
        <HeadingMenu
            icon={['fal', 'gear']}
            title={t('common:settings')}
            toggle={onToggleMenu}
            isOpened={showMenu.value}
            disabled={showMenu.value || disabled}
            data-testid="grid-settings-menu"
        >
            <div className="settings-menu" ref={settingsMenuRef}>
                {showPager && (
                    <GridColumnMenuItemGroup>
                        <MenuButton
                            title={t('recordPerPage')}
                            icon={['fal', 'table-rows']}
                            active={recPerPageExpanded.value}
                            action={() => onMenuItemClick(recPerPageExpanded)}
                        />
                        <GridColumnMenuItemContent show={recPerPageExpanded.value}>
                            <ItemsPerPage value={pageSize} onChange={onItemsPerPageChange} />
                        </GridColumnMenuItemContent>
                    </GridColumnMenuItemGroup>
                )}
                <GridColumnMenuItemGroup>
                    <MenuButton
                        title={t('lockColumns')}
                        icon={['fal', 'lock-alt']}
                        active={lockExpanded.value}
                        action={() => onMenuItemClick(lockExpanded)}
                    />
                    <GridColumnMenuItemContent show={lockExpanded.value}>
                        <MenuContent
                            items={columnsArray.filter((item) => !item.isChild).sort(compareColumnsByOrderIndex)}
                            onSubmit={onSubmit(SettingsMenuTypes.lock)}
                            onReset={onReset(SettingsMenuTypes.lock)}
                            checkedField={SettingsMenuTypes.lock}
                            oneVisibleColumn={oneVisibleColumn}
                        />
                    </GridColumnMenuItemContent>
                </GridColumnMenuItemGroup>
                {!removeHideFilter && (
                    <GridColumnMenuItemGroup>
                        <MenuButton
                            title={t('showHideColumns')}
                            icon={['fal', 'bars']}
                            active={columnExpanded.value}
                            rotation={90}
                            action={() => onMenuItemClick(columnExpanded)}
                        />
                        <GridColumnMenuItemContent show={columnExpanded.value}>
                            <MenuContent
                                items={columnsArray.filter((item) => !item.isParent).sort(compareColumnsByOrderIndex)}
                                onSubmit={onSubmit(SettingsMenuTypes.show)}
                                onReset={onReset(SettingsMenuTypes.show)}
                                checkedField={SettingsMenuTypes.show}
                                oneVisibleColumn={oneVisibleColumn}
                            />
                        </GridColumnMenuItemContent>
                    </GridColumnMenuItemGroup>
                )}
                <GridColumnMenuItemGroup>
                    <MenuButton
                        title={t('groupColumns')}
                        icon={['fal', 'stream']}
                        active={groupExpanded.value}
                        action={() => onMenuItemClick(groupExpanded)}
                    />
                    <GridColumnMenuItemContent show={groupExpanded.value}>
                        <MenuContent
                            items={columnsArray.filter((item) => !item.isParent).sort(compareColumnsByOrderIndex)}
                            onSubmit={onSubmit(SettingsMenuTypes.group)}
                            onReset={onReset(SettingsMenuTypes.group)}
                            checkedField={SettingsMenuTypes.group}
                            oneVisibleColumn={oneVisibleColumn}
                        />
                    </GridColumnMenuItemContent>
                </GridColumnMenuItemGroup>
            </div>
        </HeadingMenu>
    );
};

export default SettingsMenu;
